<template>
  <router-link
    v-if="visible"
    class="helpdesk-widget-fab"
    :class="{ 'has-unread': unreadCount > 0 }"
    :to="{ name: 'HelpdeskTickets' }"
    :aria-label="fabLabel"
  >
    <span v-if="unreadCount > 0" class="helpdesk-widget-badge">
      {{ unreadCount > 9 ? '9+' : unreadCount }}
    </span>
    <b-icon icon="headset"></b-icon>
    <span class="helpdesk-widget-fab-label">تیکت</span>
  </router-link>
</template>

<script>
import {mapGetters} from 'vuex'
import {isOdooHelpdeskRouteEnabled} from '@/plugin/support/helpdeskRoute'

const POLL_MS = 60000

export default {
  name: 'FloatingHelpdeskWidget',
  data() {
    return {
      pollTimer: null,
    }
  },
  computed: {
    ...mapGetters({
      isAdmin: 'User/isAdmin',
      enabled: 'HelpdeskTicket/isEnabled',
      ready: 'HelpdeskTicket/isReady',
      unreadCount: 'HelpdeskTicket/unreadCount',
    }),
    visible() {
      if (!this.isAdmin) {
        return false
      }
      if (!isOdooHelpdeskRouteEnabled()) {
        return false
      }
      if (this.enabled === false) {
        return false
      }
      if (!this.ready) {
        return false
      }
      return this.$route.name !== 'HelpdeskTickets'
    },
    fabLabel() {
      if (this.unreadCount > 0) {
        return `صفحه تیکت‌ها — ${this.unreadCount} پاسخ جدید`
      }
      return 'رفتن به صفحه تیکت‌ها'
    },
  },
  watch: {
    ready(value) {
      if (value && this.visible) {
        this.refreshTickets()
        this.startPolling()
      }
    },
    '$route.name'(name) {
      if (name === 'HelpdeskTickets') {
        this.stopPolling()
        return
      }
      if (this.ready) {
        this.startPolling()
      }
    },
  },
  created() {
    if (!this.isAdmin || !isOdooHelpdeskRouteEnabled()) {
      return
    }
    // Page itself loads status/tickets; avoid duplicate calls here.
    if (this.$route.name === 'HelpdeskTickets') {
      return
    }
    this.$store.dispatch('HelpdeskTicket/fetchStatus').then(() => {
      if (this.ready && this.visible) {
        this.refreshTickets()
        this.startPolling()
      }
    })
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    startPolling() {
      this.stopPolling()
      if (this.$route.name === 'HelpdeskTickets') {
        return
      }
      this.pollTimer = setInterval(() => {
        if (this.ready && this.visible) {
          this.refreshTickets({force: true})
        }
      }, POLL_MS)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    refreshTickets(options = {}) {
      return this.$store.dispatch('HelpdeskTicket/fetchTickets', options).catch(() => {
      })
    },
  },
}
</script>

<style scoped>
.helpdesk-widget-fab {
  position: fixed;
  z-index: 1150;
  bottom: max(20px, env(safe-area-inset-bottom));
  left: max(20px, env(safe-area-inset-left));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 56px;
  height: 56px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  background: #1e1e1e;
  color: #fff !important;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none !important;
  box-shadow: 0 12px 28px rgba(30, 30, 30, 0.32), 0 0 0 3px rgba(235, 61, 96, 0.22);
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.helpdesk-widget-fab.has-unread {
  background: #eb3d60;
  animation: helpdesk-pulse 1.8s ease-in-out infinite;
}

.helpdesk-widget-fab:hover,
.helpdesk-widget-fab:focus {
  background: #eb3d60;
  color: #fff !important;
  outline: none;
  box-shadow: 0 12px 28px rgba(235, 61, 96, 0.35), 0 0 0 3px rgba(30, 30, 30, 0.12);
}

.helpdesk-widget-fab-label {
  line-height: 1;
}

.helpdesk-widget-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: #fff;
  color: #eb3d60;
  font-size: 12px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(30, 30, 30, 0.2);
}

@keyframes helpdesk-pulse {
  0%, 100% {
    box-shadow: 0 12px 28px rgba(235, 61, 96, 0.35), 0 0 0 3px rgba(235, 61, 96, 0.22);
  }
  50% {
    box-shadow: 0 12px 28px rgba(235, 61, 96, 0.45), 0 0 0 6px rgba(235, 61, 96, 0.18);
  }
}

@media print {
  .helpdesk-widget-fab {
    display: none !important;
  }
}
</style>
